/**
 * API Service Layer
 * Centralized API calls using Axios
 */
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { tokenManager } from '../lib/tokenManager';
import type {
  ApiResponse,
  PaginatedResponse,
  User,
  CreateUserInput,
  UpdateUserInput,
  UserFilters,
  Company,
  CreateCompanyInput,
  UpdateCompanyInput,
  CompanyFilters,
  BankAccount,
  CreateAccountInput,
  UpdateAccountInput,
  AccountFilters,
  Transaction,
  CreateTransactionInput,
  UpdateTransactionInput,
  TransactionFilters,
  TransactionSummary,
  Statement,
  CreateStatementInput,
  UpdateStatementInput,
  StatementFilters,
  MutualFund,
  CreateMutualFundInput,
  UpdateMutualFundInput,
  MutualFundFilters,
  MutualFundSummary,
  LoginInput,
  AuthResponse,
} from '../types/api.types';

// Create axios instance
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: send cookies with requests
});

// Flag to prevent redirect loops
let isRefreshing = false;

// Queue for requests waiting on token refresh
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenManager.getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh or login endpoints
      if (originalRequest.url?.includes('/auth/refresh') || 
          originalRequest.url?.includes('/auth/login')) {
        tokenManager.clearToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await api.post<ApiResponse<{ accessToken: string }>>('/auth/refresh');
        const newToken = response.data.data?.accessToken;
        
        if (newToken) {
          tokenManager.setToken(newToken);
          processQueue(null, newToken);
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        } else {
          throw new Error('No token in refresh response');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenManager.clearToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Helper to build query string
const buildQueryString = (params?: object): string => {
  if (!params) return '';
  const filtered = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  return filtered.length ? `?${filtered.join('&')}` : '';
};

// Generic API methods
const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data;
};

const post = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data.data;
};

const put = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  // Use POST with X-HTTP-Method-Override for hosts that block PUT
  const response = await api.post<ApiResponse<T>>(url, data, {
    ...config,
    headers: { ...config?.headers, 'X-HTTP-Method-Override': 'PUT' }
  });
  return response.data.data;
};

const patch = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
  // Use POST with X-HTTP-Method-Override for hosts that block PATCH
  const response = await api.post<ApiResponse<T>>(url, data, {
    ...config,
    headers: { ...config?.headers, 'X-HTTP-Method-Override': 'PATCH' }
  });
  return response.data.data;
};

const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  // Use POST with X-HTTP-Method-Override for hosts that block DELETE
  const response = await api.post<ApiResponse<T>>(url, undefined, {
    ...config,
    headers: { ...config?.headers, 'X-HTTP-Method-Override': 'DELETE' }
  });
  return response.data.data;
};

// Paginated response type matching backend
interface PaginatedApiResponse<T> {
  success: boolean;
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// For paginated endpoints - returns full response with meta
const getPaginated = async <T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedApiResponse<T>> => {
  const response = await api.get<PaginatedApiResponse<T>>(url, config);
  return response.data;
};

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: async (data: LoginInput & { remember?: boolean }) => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', data);
    const result = response.data.data;
    if (result?.accessToken) {
      tokenManager.setToken(result.accessToken, data.remember);
    }
    return result;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      tokenManager.clearToken();
    }
  },
  
  me: () => get<User>('/auth/me'),
  
  refreshToken: async () => {
    const response = await api.post<ApiResponse<{ accessToken: string }>>('/auth/refresh');
    const result = response.data.data;
    if (result?.accessToken) {
      tokenManager.setToken(result.accessToken);
    }
    return result;
  },
  
  /**
   * Check if user has a valid session (attempt refresh)
   * Only attempts refresh if there's a session flag from previous login
   * This avoids 401 errors on fresh page loads with no prior session
   */
  checkSession: async (): Promise<User | null> => {
    // Skip refresh attempt if we've never logged in before
    if (!tokenManager.mayHaveSession()) {
      return null;
    }
    
    try {
      // We had a session before, try to restore it
      await authApi.refreshToken();
      return await authApi.me();
    } catch {
      // Session expired or invalid - clear the flag
      tokenManager.clearToken();
      return null;
    }
  },
};

// ============================================
// Users API
// ============================================
export const usersApi = {
  getAll: (filters?: UserFilters) => 
    get<PaginatedResponse<User>>(`/users${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<User>(`/users/${id}`),
  
  create: (data: CreateUserInput) => 
    post<User>('/users', data),
  
  update: (id: string, data: UpdateUserInput) => 
    put<User>(`/users/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/users/${id}`),
  
  updateStatus: (id: string, isActive: boolean) => 
    patch<User>(`/users/${id}/status`, { isActive }),
  
  resetPassword: (id: string, password: string) => 
    patch<void>(`/users/${id}/password`, { password }),
  
  changeMyPassword: (currentPassword: string, newPassword: string) =>
    put<void>('/users/me/password', { currentPassword, newPassword }),
};

// ============================================
// Companies API
// ============================================
export const companiesApi = {
  getAll: (filters?: CompanyFilters) => 
    get<PaginatedResponse<Company>>(`/companies${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<Company>(`/companies/${id}`),
  
  getSubsidiaries: (id: string) => 
    get<Company[]>(`/companies/${id}/subsidiaries`),
  
  getHoldings: () => 
    get<Company[]>('/companies/holdings'),
  
  create: (data: CreateCompanyInput) => 
    post<Company>('/companies', data),
  
  update: (id: string, data: UpdateCompanyInput) => 
    patch<Company>(`/companies/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/companies/${id}`),
};

// ============================================
// Bank Accounts API
// ============================================
export const accountsApi = {
  getAll: (filters?: AccountFilters) => 
    get<PaginatedResponse<BankAccount>>(`/accounts${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<BankAccount>(`/accounts/${id}`),
  
  getByCompany: (companyId: string) => 
    get<BankAccount[]>(`/accounts/company/${companyId}`),
  
  create: (data: CreateAccountInput) => 
    post<BankAccount>('/accounts', data),
  
  update: (id: string, data: UpdateAccountInput) => 
    patch<BankAccount>(`/accounts/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/accounts/${id}`),
};

// ============================================
// Transactions API
// ============================================
export const transactionsApi = {
  getAll: (filters?: TransactionFilters) => 
    getPaginated<Transaction>(`/transactions${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<Transaction>(`/transactions/${id}`),
  
  getByAccount: (accountId: string, filters?: TransactionFilters) => 
    getPaginated<Transaction>(`/transactions/account/${accountId}${buildQueryString(filters)}`),
  
  getSummary: (filters?: TransactionFilters) => 
    get<TransactionSummary>(`/transactions/summary${buildQueryString(filters)}`),
  
  getStats: () => 
    get<{ pending: number; reconciled: number; flagged: number; total: number }>('/transactions/stats'),
  
  create: (data: CreateTransactionInput) => 
    post<Transaction>('/transactions', data),
  
  update: (id: string, data: UpdateTransactionInput) => 
    put<Transaction>(`/transactions/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/transactions/${id}`),
  
  reconcile: (id: string) => 
    patch<Transaction>(`/transactions/${id}/reconcile`),
  
  bulkReconcile: (ids: string[]) => 
    post<{ updated: number }>('/transactions/reconcile', { ids }),
};

// ============================================
// Statements API
// ============================================
export const statementsApi = {
  getAll: (filters?: StatementFilters) => 
    get<PaginatedResponse<Statement>>(`/statements${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<Statement>(`/statements/${id}`),
  
  getByAccount: (accountId: string, filters?: StatementFilters) => 
    get<PaginatedResponse<Statement>>(`/statements/account/${accountId}${buildQueryString(filters)}`),
  
  create: (data: CreateStatementInput) => 
    post<Statement>('/statements', data),
  
  update: (id: string, data: UpdateStatementInput) => 
    patch<Statement>(`/statements/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/statements/${id}`),
  
  process: (id: string) => 
    post<Statement>(`/statements/${id}/process`),
  
  upload: (accountId: string, file: File, month: number, year: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', String(month));
    formData.append('year', String(year));
    return post<Statement>(`/statements/upload/${accountId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============================================
// Mutual Funds API
// ============================================
export const mutualFundsApi = {
  getAll: (filters?: MutualFundFilters) => 
    get<PaginatedResponse<MutualFund>>(`/mutual-funds${buildQueryString(filters)}`),
  
  getById: (id: string) => 
    get<MutualFund>(`/mutual-funds/${id}`),
  
  getByCompany: (companyId: string) => 
    get<MutualFund[]>(`/mutual-funds/company/${companyId}`),
  
  getSummary: () => 
    get<MutualFundSummary>('/mutual-funds/summary'),
  
  create: (data: CreateMutualFundInput) => 
    post<MutualFund>('/mutual-funds', data),
  
  update: (id: string, data: UpdateMutualFundInput) => 
    patch<MutualFund>(`/mutual-funds/${id}`, data),
  
  delete: (id: string) => 
    del<void>(`/mutual-funds/${id}`),
  
  redeem: (id: string) => 
    patch<MutualFund>(`/mutual-funds/${id}/redeem`),
};

// ============================================
// Uploads API
// ============================================
export interface UploadedFile {
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export const uploadsApi = {
  upload: async (type: string, files: File[]): Promise<UploadedFile[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    const response = await api.post<{ success: boolean; data: UploadedFile[] }>(
      `/uploads/${type}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },
  
  list: (type: string) => 
    get<{ files: UploadedFile[] }>(`/uploads/${type}`),
  
  delete: (type: string, fileName: string) => 
    del<void>(`/uploads/${type}/${fileName}`),
};

// Export axios instance for custom requests
export { api };
